import { apiClient } from '@services/api-client'
import { SNA_PREFIX } from '@services/utils'
import { useAddTicketComment } from '@services/tickets/mutations/useAddTicketComment'
import type { TicketAttachment, TicketDetail } from '@services/tickets/types'
import { Paperclip, Send } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

import { AttachmentPicker } from './AttachmentPicker'
import { useIntlLocale } from '@/i18n/languages'

async function downloadAttachment(ticketId: string, attachment: TicketAttachment) {
  const res = await apiClient.get<Blob>(
    `${SNA_PREFIX}tickets/${ticketId}/attachments/${attachment.id}/download/`,
    { responseType: 'blob' }
  )
  const url = URL.createObjectURL(res.data)
  const link = document.createElement('a')
  link.href = url
  link.download = attachment.name ?? 'attachment'
  link.click()
  URL.revokeObjectURL(url)
}

export function AttachmentChips({
  ticketId,
  attachments
}: {
  ticketId: string
  attachments: TicketAttachment[]
}) {
  if (!attachments.length) return null
  return (
    <div className="flex flex-wrap gap-2">
      {attachments.map((attachment) => (
        <button
          key={attachment.id}
          type="button"
          onClick={() => downloadAttachment(ticketId, attachment)}
          className="flex items-center gap-1.5 rounded-full border border-ink/10 bg-white px-2.5 py-1 text-xs text-ink/70 transition-colors hover:border-primary hover:text-primary"
        >
          <Paperclip className="h-3 w-3" />
          <span className="max-w-[160px] truncate">{attachment.name}</span>
        </button>
      ))}
    </div>
  )
}

export function TicketCommentThread({
  ticket,
  isAdminView = false
}: {
  ticket: TicketDetail
  isAdminView?: boolean
}) {
  const { t } = useTranslation('tickets')
  const addComment = useAddTicketComment()
  const [text, setText] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const locale = useIntlLocale()

  const canSend = text.trim().length > 0 && !addComment.isPending

  const send = () => {
    if (!canSend) return
    addComment.mutate(
      { ticketId: ticket.id, text: text.trim(), attachments: files },
      {
        onSuccess: () => {
          setText('')
          setFiles([])
        }
      }
    )
  }

  return (
    <div className="space-y-4">
      <h3 className="m-0 font-display text-base font-bold text-ink">
        {t('detail.comments')} ({ticket.comments.length})
      </h3>

      <div className="space-y-3">
        {ticket.comments.length === 0 && (
          <p className="m-0 text-sm text-muted-foreground">
            {t('detail.no_comments')}
          </p>
        )}
        {ticket.comments.map((comment) => {
          const isAdminComment = comment.author.role === 'ADMIN'
          // "mine" bubbles align right: admin comments in admin view,
          // tenant comments in tenant view.
          const mine = isAdminView ? isAdminComment : !isAdminComment
          return (
            <div
              key={comment.id}
              className={cn('flex', mine ? 'justify-end' : 'justify-start')}
            >
              <div
                className={cn(
                  'max-w-[80%] space-y-2 rounded-2xl px-4 py-3',
                  mine ? 'bg-primary/10' : 'bg-ink/[0.04]'
                )}
              >
                <div className="text-xs font-semibold text-ink/60">
                  {isAdminComment
                    ? t('detail.support')
                    : `${comment.author.first_name ?? ''} ${comment.author.last_name ?? ''}`.trim() ||
                      t('detail.tenant')}
                  {' · '}
                  {new Date(comment.created_at).toLocaleString(locale)}
                </div>
                <p className="m-0 whitespace-pre-wrap text-sm text-ink">
                  {comment.text}
                </p>
                <AttachmentChips
                  ticketId={ticket.id}
                  attachments={comment.attachments}
                />
              </div>
            </div>
          )
        })}
      </div>

      {ticket.status !== 'CLOSED' && (
        <div className="space-y-2 rounded-2xl border border-ink/[0.08] p-3">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            placeholder={t('detail.comment_placeholder')}
            className="w-full resize-none rounded-xl border border-input bg-white px-3.5 py-2.5 text-sm text-ink placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none"
          />
          <div className="flex items-center justify-between gap-3">
            <AttachmentPicker files={files} onChange={setFiles} />
            <button
              type="button"
              onClick={send}
              disabled={!canSend}
              aria-label={t('detail.send')}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-0 bg-primary text-white transition-transform hover:scale-105 disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
